<script setup lang="ts">
const props = defineProps<{
  error: any | Ref<any>
}>()

const emit = defineEmits(['clearError'])

const { t } = useI18n({ useScope: 'local' })

const error = ref(unref(props.error))

console.error(`Tairo error:`, error.value)

// TODO: this sucks
// @ts-ignore
const isWrongCenterError = error.message && error.message.includes('wrong center')

const title = computed(() => {
  if (isWrongCenterError) {
    return t('wrongCenter.title')
  }

  if (error.value?.statusCode === 401) {
    return t('unauthorized.title')
  }

  if (error.value?.statusCode === 403) {
    return t('forbidden.title')
  }

  if (error.value?.statusCode === 404) {
    return t('notFound.title')
  }

  if (error.value?.statusCode) {
    return t('generic.titleWithCode', { code: error.value?.statusCode })
  }

  return t('generic.title')
})

const description = computed(() => {
  if (isWrongCenterError) {
    return t('wrongCenter.description')
  }

  if (error.value?.statusCode === 401) {
    return t('unauthorized.description')
  }

  if (error.value?.statusCode === 403) {
    return t('forbidden.description')
  }

  if (error.value?.statusCode === 404) {
    return t('notFound.description')
  }

  return t('generic.description')
})

const clicked = ref(0)
const handleError = function () {
  clicked.value += 1
  // This is terrible. We need to fix Nuxt error + routing issues
  if (clicked.value > 1 && window) {
    // hard refresh, due to issue with nav being broken
    window.location.href = '/'
  }
  else {
    clearError({ redirect: '/' })
  }
}
const retry = () => clearError()

// This is a quickfix to allow the parent to clear the error after navigating away from the error page
// According to the Nuxt docs, errors should be cleared automatically on navigation, but that's not the case.
// The parent can do this: <AppError :error="error" @clearError="() => (error.value = null)" />
const router = useRouter()
router.afterEach(() => emit('clearError'))

// Show/hide demo stack trace
const showStackTrace = ref(true)
</script>

<template>
  <div class="pb-16">
    <BasePlaceholderPage
      :title="title"
      :subtitle="description"
      image-size="md"
      class="relative !items-end"
    >
      <template #image>
        <div class="mb-4 flex items-center justify-center">
          <PublicLogo />
        </div>
      </template>

      <div class="mt-4">
        <div
          class="text-muted-400/20 dark:text-muted-400/10 absolute inset-x-0 top-1/3 -translate-y-1/2 text-[13rem] font-bold sm:text-[20rem]"
        >
          <span>{{ error?.statusCode }}</span>
        </div>
        <div
          class="mx-auto flex w-full max-w-xs items-center justify-center gap-2"
        >
          <BaseButton
            shape="curved"
            class="mx-auto !h-12 w-full max-w-[160px] items-center gap-2"
            @click="handleError"
          >
            <Icon name="ph:arrow-left" />
            {{ t('buttons.backToHome') }}
          </BaseButton>
          
        </div>
        
      </div>
    </BasePlaceholderPage>

    
  </div>
</template>

<style>
.stack {
  @apply text-sm;
}
.stack.internal {
  @apply pl-4 text-xs opacity-100;
}
</style>

<i18n lang="json">
{
  "en": {
    "wrongCenter": {
      "title": "Logged in to the wrong website",
      "description": "Please contact the administrator of this website. This user account is registered with one of the other partners in the collaboration."
    },
    "unauthorized": {
      "title": "Not authorized",
      "description": "You are not authorized to access this page."
    },
    "forbidden": {
      "title": "Access forbidden",
      "description": "You do not have access rights to this page."
    },
    "notFound": {
      "title": "Page not found",
      "description": "We couldn't find the page you were looking for, please contact a system administrator or try again later."
    },
    "generic": {
      "title": "Oops... something went wrong!",
      "titleWithCode": "Oops... something went wrong! (error code: {code})",
      "description": "An error has occurred. If the problem persists, please contact a system administrator or try again later."
    },
    "buttons": {
      "backToHome": "Back to home",
      "clearError": "Clear Error"
    },
    "dev": {
      "stacktraceToggle": "{action} Stacktrace (dev)",
      "show": "Show",
      "hide": "Hide",
      "stacktraceDescription": "This is a dev only stacktrace, you won't see it in production."
    }
  },
  "nl": {
    "wrongCenter": {
      "title": "Ingelogd op de verkeerde website",
      "description": "Neem contact op met de beheerder van deze website. Dit gebruikersaccount is geregistreerd bij een van de andere partners in het samenwerkingsverband."
    },
    "unauthorized": {
      "title": "Niet geautoriseerd",
      "description": "Je bent niet geautoriseerd om deze pagina te bekijken."
    },
    "forbidden": {
      "title": "Verboden toegang",
      "description": "Je hebt geen toegangsrechten voor deze pagina."
    },
    "notFound": {
      "title": "Pagina niet gevonden",
      "description": "We konden de pagina die je zocht niet vinden. Neem contact op met een systeembeheerder of probeer het later opnieuw."
    },
    "generic": {
      "title": "Oeps... er ging iets mis!",
      "titleWithCode": "Oeps... er ging iets mis! (foutcode: {code})",
      "description": "Er is een fout opgetreden. Als het probleem blijft bestaan, neem dan contact op met een systeembeheerder of probeer het later opnieuw."
    },
    "buttons": {
      "backToHome": "Terug naar home",
      "clearError": "Fout wissen"
    },
    "dev": {
      "stacktraceToggle": "{action} Stacktrace (dev)",
      "show": "Tonen",
      "hide": "Verbergen",
      "stacktraceDescription": "Dit is een stacktrace alleen voor ontwikkelaars, je ziet dit niet in productie."
    }
  }
}
</i18n>
